// SearchBar.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

/* 1. Mapa ruta ↔︎ título (fuera del componente) */
const PAGE_TITLES = {
  "/inicio": "Inicio",
  "/tareas": "Tareas",
  "/calendario": "Calendario",
  "/cotizacion": "Cotizaciones",
  "/remisiones": "Remisiones",
  "/pedidos": "Ordenes de Compra (Clientes)",
  "/orden": "Ordenes de Compra (Proveedores)",
  "/articulos-venta": "Artículos de venta",
  "/articulos-compra": "Artículos de compra",
  "/categorias": "Categorías",
  "/todos": "Contactos",
  "/clientes": "Clientes",
  "/proveedores": "Proveedores",
  "/usuarios": "Usuarios",
  "/personalizacion": "Personalizacion",
  "/facturas-de-proveedores": "Facturas de Proveedores",
  "/facturas-de-clientes": "Facturas de Clientes",
  "/calculador": "Calculador de Costos",
  "/grupos": "Grupos",
  "/generador": "Generador de Obra",
};

/* 2. Normalizador */
const normalize = (str) =>
  str
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "-");

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false); // ⬅️ visibilidad
  const navigate = useNavigate();

  /* cargar historial pero NO mostrarlo de entrada */
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("searchHistory")) || [];
    setHistory(stored);
  }, []);

  /* filtrar mientras escribe */
  const handleChange = (e) => {
    const query = e.target.value;
    setSearchTerm(query);

    if (!query.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const filtered = Object.keys(PAGE_TITLES)
      .filter((p) => PAGE_TITLES[p].toLowerCase().includes(query.toLowerCase()))
      .map((p) => PAGE_TITLES[p]);

    setSuggestions(filtered);
    setShowDropdown(true);
  };

  /* guardar en historial */
  const saveToHistory = (text) => {
    if (!text || history.includes(text)) return;
    const updated = [text, ...history].slice(0, 20);
    localStorage.setItem("searchHistory", JSON.stringify(updated));
    setHistory(updated);
  };

  /* navegación */
  const goToPage = (text) => {
    if (text === "Inicio") return navigate("/");

    const path = Object.keys(PAGE_TITLES).find(
      (p) => normalize(PAGE_TITLES[p]) === normalize(text)
    );
    if (path) navigate(path);
    else alert("Página no encontrada.");
  };

  /* submit: Enter */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    saveToHistory(searchTerm);
    goToPage(searchTerm);
    setShowDropdown(false); // ocultar
  };

  /* clic sugerencia */
  const handleSuggestionClick = (text) => {
    setSearchTerm(text);
    saveToHistory(text);
    goToPage(text);
    setShowDropdown(false); // ocultar
  };

  return (
    <div className="search-bar" onSubmit={handleSubmit}>
      <Search size={16} />
      <input
        className="search-input"
        type="text"
        placeholder="Buscar..."
        value={searchTerm}
        onChange={handleChange}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
        onFocus={() => {
          if (searchTerm.trim()) setShowDropdown(true);
        }}
        /* pequeño timeout para permitir clic en la lista antes de ocultar */
        onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
      />

      {showDropdown && suggestions.length > 0 && (
        <ul className="suggestions-list">
          {suggestions.map((text, idx) => {
            const fromHistory = history.includes(text);
            return (
              <li key={idx} onMouseDown={() => handleSuggestionClick(text)}>
                <i
                  className={`bi ${
                    fromHistory ? "bi-clock-history" : "bi-search"
                  }`}
                />
                <span>{text}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
